import React, { FC, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertTitle, Box, Button, Grid, Typography, FormControl } from '@mui/material';
import TransportDatePicker from 'components/shared/transport-date-picker/transport-date-picker.component';
import { TransportServices } from 'models/transport';
import { transportDateHelper } from 'utils/transport-date-helper';
import TransportDeadline from 'components/shared/transport-deadline/transport-deadline.component';
import { AddTransportContext } from 'pages/transport/add-transport/add-transport-context';

type TransportWhenStepProps = {
    onNavigateNext: () => void;
    onNavigateBack: () => void;
};
const TransportWhenStep: FC<TransportWhenStepProps> = ({ onNavigateNext, onNavigateBack }: TransportWhenStepProps) => {
    const { t: translate } = useTranslation(['transport', 'common']);
    const { transportOrderReq, setTransportOrderReq } = useContext(AddTransportContext);
    const [transportDate, setTransportDate] = useState<string>('');

    useEffect(() => {
        setTransportDate(
            transportDateHelper.nextWorkingDayAsString(
                transportOrderReq.date,
                transportOrderReq.serviceCode === TransportServices.PICKUP,
            ),
        );
    }, [transportOrderReq.date]);

    const dateChanged = (date: Date | null) => {
        setTransportOrderReq({
            ...{ ...transportOrderReq, date: date },
        });
    };
    const deadlineChanged = (deadline: string) => {
        setTransportOrderReq({
            ...{ ...transportOrderReq, deadline: deadline },
        });
    };

    const sameDayDelivery = transportDateHelper.isSameDayDelivery(transportOrderReq.date);

    return (
        <Box>
            <Box mb={4}>
                <Box mb={2}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <TransportDatePicker
                                    date={transportOrderReq.date}
                                    onDateChange={dateChanged}
                                    type={transportOrderReq.serviceCode}
                                />

                                {transportOrderReq.serviceCode === TransportServices.DELIVERY && sameDayDelivery && (
                                    <Box mt={1}>
                                        <Alert severity="warning">{translate('transport:SAME_DAY_DELIVERY')}</Alert>
                                    </Box>
                                )}
                            </FormControl>
                        </Grid>
                        {transportOrderReq.serviceCode === TransportServices.DELIVERY ? (
                            <Grid item xs={12} md={6}>
                                <TransportDeadline
                                    readOnly={false}
                                    deadline={transportOrderReq.deadline}
                                    onDeadlineChange={deadlineChanged}
                                />
                            </Grid>
                        ) : undefined}
                    </Grid>
                </Box>
                <Box>
                    <Alert icon={false} severity="info">
                        <AlertTitle>
                            <Typography variant="subtitle2">
                                {transportOrderReq.serviceCode === TransportServices.PICKUP
                                    ? translate('transport:LATEST_DATE_DELIVERY')
                                    : translate('transport:PICKUP_FROM_YOU_DATE')}
                            </Typography>
                        </AlertTitle>
                        <Box>{translate(transportDateHelper.transportDateFormatter(transportDate, 'date'))}</Box>
                    </Alert>
                </Box>
            </Box>
            <Box display="flex">
                <Box flexGrow={1}>
                    <Button onClick={onNavigateBack}>{translate('common:BACK')}</Button>
                </Box>
                <Box>
                    <Button
                        variant="contained"
                        disabled={!transportOrderReq.date}
                        onClick={() => {
                            if (transportOrderReq.date) {
                                onNavigateNext();
                            }
                        }}
                    >
                        {translate('common:NEXT_STEP')}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

export default TransportWhenStep;
